/**
 * Standstill Calculator Module
 *
 * Pure function that detects standstill periods from vehicle route packet data
 * by analyzing transitions of the `other_data.stand_still` flag with additional
 * speed and duration threshold validation.
 *
 * Logic:
 * 1. Detect standstill periods from stand_still flag transitions (false→true, true→false)
 * 2. Validate each period against speed threshold (< 5 km/h)
 * 3. Validate each period against duration threshold (≥ 60 seconds)
 * 4. Use first available coordinate in the standstill period
 *
 * Produces Standstill_Entry objects compatible with the existing
 * clusterStandstills / createStandstillPopupElement pipeline.
 */

/**
 * Default configuration for standstill detection
 */
const DEFAULT_CONFIG = {
  speedThreshold: 5, // km/h - maximum average speed to be considered standstill
  durationThreshold: 60, // seconds - minimum duration to be considered a standstill
};

/**
 * Export default config for external use
 */
export { DEFAULT_CONFIG };

/**
 * Checks whether a GPS object has a valid position (lat/lng are numbers).
 * @param {Object} gps
 * @returns {boolean}
 */
function hasValidGps(gps) {
  return (
    gps &&
    gps.position &&
    typeof gps.position.lat === 'number' &&
    typeof gps.position.lng === 'number'
  );
}

/**
 * Extracts speed from packet in km/h.
 * Checks velocity.speed (m/s) first, then other_data.gps_speed (m/s).
 * @param {Object} packet
 * @returns {number|null} Speed in km/h, or null if not available
 */
function extractSpeed(packet) {
  // Check velocity.speed first (m/s)
  if (packet.velocity && typeof packet.velocity.speed === 'number') {
    return packet.velocity.speed * 3.6; // Convert m/s to km/h
  }

  // Fallback to other_data.gps_speed (m/s)
  if (packet.other_data && typeof packet.other_data.gps_speed === 'number') {
    return packet.other_data.gps_speed * 3.6; // Convert m/s to km/h
  }

  return null;
}

/**
 * Transforms address from packet GPS format to internal model.
 * @param {Object} gps
 * @returns {Object|null}
 */
function extractAddress(gps) {
  if (!gps || !gps.position || !gps.position.address) {
    return null;
  }

  const addr = gps.position.address;

  if (!addr.name && !addr.street && !addr.city && !addr.country) {
    return null;
  }

  return {
    name: addr.name || '',
    street: addr.street || '',
    city: addr.city || '',
    region: addr.region || '',
    country: addr.country || '',
    postalCode: addr.postal_code || '',
  };
}

/**
 * Calculates standstill periods from an array of vehicle route packets.
 *
 * The function is pure (no side effects), defensive (never throws),
 * and always returns an array.
 *
 * @param {Array<Object>} packets - Array of packet data from resData.data
 * @param {Object} config - Configuration object with speedThreshold and durationThreshold
 * @returns {Array<Object>} Array of Standstill_Entry objects
 */
export function calculateStandstills(packets, config = {}) {
  // Merge config with defaults
  const cfg = { ...DEFAULT_CONFIG, ...config };

  // Input validation: return [] if packets is not an array or is empty
  if (!Array.isArray(packets) || packets.length === 0) {
    return [];
  }

  // Filter packets with invalid server_time (not a number or negative)
  const validPackets = packets.filter(
    (p) => p && typeof p.server_time === 'number' && p.server_time >= 0
  );

  if (validPackets.length === 0) {
    return [];
  }

  // Sort by server_time ascending, with source_time as tiebreaker
  const sorted = validPackets.slice().sort((a, b) => {
    const diff = a.server_time - b.server_time;
    if (diff !== 0) return diff;
    // Use source_time as tiebreaker when server_time is equal
    const aSource = typeof a.source_time === 'number' ? a.source_time : 0;
    const bSource = typeof b.source_time === 'number' ? b.source_time : 0;
    return aSource - bSource;
  });

  // Normalize stand_still flag: treat non-boolean as false
  // Support both root-level and other_data-nested field locations
  const flags = sorted.map((p) => {
    // Check root level first (actual API format), then other_data (design doc format)
    const raw =
      typeof p.stand_still === 'boolean'
        ? p.stand_still
        : p.other_data && typeof p.other_data.stand_still === 'boolean'
          ? p.other_data.stand_still
          : false;
    return raw;
  });

  // Detect transitions of stand_still flag
  //   false→true  = start of a standstill period
  //   true→false  = end of a standstill period (previous packet is the end)
  //   First packet true = start
  //   Last packet true  = open-ended period (endTime = last packet server_time)
  const periods = []; // Array of { startIdx, endIdx }
  let periodStart = null;

  for (let i = 0; i < sorted.length; i++) {
    const current = flags[i];
    const prev = i > 0 ? flags[i - 1] : false;

    if (current && !prev) {
      // Transition false→true (or first packet is true)
      periodStart = i;
    } else if (!current && prev && periodStart !== null) {
      // Transition true→false — end is previous packet
      periods.push({ startIdx: periodStart, endIdx: i - 1 });
      periodStart = null;
    }
  }

  // If the last packet is still true, close the open-ended period
  if (periodStart !== null) {
    periods.push({ startIdx: periodStart, endIdx: sorted.length - 1 });
  }

  // Build Standstill_Entry for each period with threshold validation
  const results = [];
  let entryIndex = 0;

  for (const period of periods) {
    const startPacket = sorted[period.startIdx];
    const endPacket = sorted[period.endIdx];

    // Skip periods with invalid GPS on the start packet
    if (!hasValidGps(startPacket.gps)) {
      continue;
    }

    // Calculate startTime using standstill_start_time (if valid) or fallback to source_time/server_time
    // Support both root-level and other_data-nested field locations
    const sstFromRoot =
      typeof startPacket.standstill_start_time === 'number' && startPacket.standstill_start_time > 0
        ? startPacket.standstill_start_time
        : null;
    const sstFromOtherData =
      startPacket.other_data &&
      typeof startPacket.other_data.standstill_start_time === 'number' &&
      startPacket.other_data.standstill_start_time > 0
        ? startPacket.other_data.standstill_start_time
        : null;
    const sst = sstFromRoot || sstFromOtherData || startPacket.source_time || startPacket.server_time;

    const endTime = endPacket.source_time || endPacket.server_time;

    // Duration with clamping to minimum 0
    const duration = Math.max(0, endTime - sst);

    // THRESHOLD VALIDATION 1: Duration must meet minimum threshold
    if (duration < cfg.durationThreshold * 1000) {
      continue; // Skip this standstill - too short
    }

    // THRESHOLD VALIDATION 2: Average speed must be below threshold
    // Calculate average speed across all packets in this period
    const periodPackets = sorted.slice(period.startIdx, period.endIdx + 1);
    const speeds = periodPackets
      .map(p => extractSpeed(p))
      .filter(s => s !== null);

    if (speeds.length > 0) {
      const avgSpeed = speeds.reduce((sum, s) => sum + s, 0) / speeds.length;

      // Skip if average speed exceeds threshold
      if (avgSpeed >= cfg.speedThreshold) {
        continue; // Skip this standstill - moving too fast
      }
    }
    // If no speed data available, still include the standstill (backward compatible)

    // Build the Standstill_Entry
    results.push({
      id: `standstill-${sst}-${entryIndex}`,
      startTime: sst,
      endTime: endTime,
      duration: duration,
      position: {
        lat: startPacket.gps.position.lat,
        lng: startPacket.gps.position.lng,
        isValid: true,
      },
      address: extractAddress(startPacket.gps),
      gps: startPacket.gps,
    });

    entryIndex++;
  }

  return results;
}
